import React, { useMemo } from "react"
import {
  availableAvoidOptions,
  availableLoadTypes,
  getAvailableAvoidOptions,
  optionLabel,
} from "./carRouteOptions"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"
import { AvoidType, LoadType } from "@/api/adHocCommands"
import type { RoutingProvider } from "@/reducers/serviceConfigSlice"
import { isMobile } from "@/utils/detectDevice"
import { MultiSelect } from "@/components/ui/multi-select"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import { RouteStop } from "@/reducers/routes2gSlice"
import { Location } from "@/reducers/locationsSlice"
import ClearRouteButton from "@/components/maps/ClearRouteButton"
import { useRouteOptions } from "@/hooks/useRouteOptions"

interface RouteOptionsSelectProps {
  driverJid: string
  onTollsToggle?: () => void
  tollsToggleLabel?: string
  stops: RouteStop[] | undefined
  className?: string
}

const formatStopAddress = (location: Location) => {
  if (location.countryCode && location.locality) {
    return `${location.countryCode}, ${location.postalCode ? location.postalCode : ""} ${location.locality}`
  }

  if (location.lat && location.lon) {
    const lat = parseFloat(location.lat).toFixed(5)
    const lon = parseFloat(location.lon).toFixed(5)
    return `${lat}, ${lon}`
  }

  if (location.formattedAddress) {
    return location.formattedAddress
  }

  return "?"
}

// Generate summary text
export const getOptionsSummaryText = ({
  stops,
  avoid,
  loadType,
  routingProvider = "here",
}: {
  stops: RouteStop[] | undefined
  avoid: AvoidType[]
  loadType: LoadType[]
  routingProvider?: RoutingProvider
}) => {
  const hasAvoidanceInStops = stops?.some((stop) =>
    stop.avoid?.includes("tollRoads"),
  )
  const globalAvoidsTollRoads = avoid.includes("tollRoads")

  if (
    avoid.length === 0 &&
    loadType.length === 0 &&
    !hasAvoidanceInStops &&
    !stops?.some((stop) => stop.avoid !== undefined)
  ) {
    return null
  }

  const parts = []

  if (avoid.length > 0) {
    const avoidLabels = avoid
      .map((optionValue) => {
        const option = getAvailableAvoidOptions(
          availableAvoidOptions,
          routingProvider,
        ).find((opt) => opt.value === optionValue)
        return option ? optionLabel(i18n.t, option).toLowerCase() : optionValue
      })
      .join(", ")
    parts.push(
      i18n.t("maps.routeOptionsSelect.summary.avoid", { labels: avoidLabels }),
    )
  }

  if (loadType.length > 0) {
    const loadLabels = loadType
      .map((optionValue) => {
        const option = availableLoadTypes.find(
          (opt) => opt.value === optionValue,
        )
        return option ? optionLabel(i18n.t, option).toLowerCase() : optionValue
      })
      .join(", ")
    parts.push(
      i18n.t("maps.routeOptionsSelect.summary.allow", { labels: loadLabels }),
    )
  }

  if (stops && stops.length > 0) {
    // Check for stops with different avoidance than global settings
    const differentAvoidanceStops = stops
      .map((stop, index) => ({ stop, index }))
      .filter(({ stop }) => {
        if (stop.avoid === undefined) return false

        const stopAvoidsTollRoads = stop.avoid.includes("tollRoads")
        // Show if different from global setting
        return stopAvoidsTollRoads !== globalAvoidsTollRoads
      })

    if (differentAvoidanceStops.length > 0) {
      const segments = differentAvoidanceStops
        .map(({ stop, index }) => {
          const to = formatStopAddress(stop.location)
          const stopAvoidsTollRoads = stop.avoid?.includes("tollRoads")

          if (index === 0) {
            return stopAvoidsTollRoads
              ? i18n.t("maps.routeOptionsSelect.summary.avoidTollsFromStart", {
                  to,
                })
              : i18n.t("maps.routeOptionsSelect.summary.allowTollsFromStart", {
                  to,
                })
          }
          const from = formatStopAddress(stops[index - 1].location)
          return stopAvoidsTollRoads
            ? i18n.t("maps.routeOptionsSelect.summary.avoidTolls", { from, to })
            : i18n.t("maps.routeOptionsSelect.summary.allowTolls", { from, to })
        })
        .join(", ")
      parts.push(segments)
    }
  }
  if (parts.length === 0) {
    return null
  }
  return i18n.t("maps.routeOptionsSelect.summary.sentence", {
    parts: parts.join(i18n.t("maps.routeOptionsSelect.summary.and")),
  })
}

const UpdateIndicator = () => (
  <div className="absolute left-1/2 top-1/2 z-[1000] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg p-4">
    <Loader2 className="h-6 w-6 animate-spin" />
  </div>
)

const RouteOptionsSelect: React.FC<RouteOptionsSelectProps> = ({
  driverJid,
  onTollsToggle,
  tollsToggleLabel,
  stops,
  className,
}) => {
  const { t } = useTranslation()
  const {
    routingProvider,
    avoidOptions,
    loadTypeOptions,
    selectedAvoid,
    selectedLoadTypes,
    pendingAvoid,
    pendingLoadTypes,
    setPendingAvoid,
    setPendingLoadTypes,
    applyAvoid,
    applyLoadTypes,
    isUpdating,
  } = useRouteOptions(driverJid)

  const translatedAvoidOptions = useMemo(
    () =>
      avoidOptions.map((option) => ({
        ...option,
        label: optionLabel(t, option),
      })),
    [avoidOptions, t],
  )
  const translatedLoadTypeOptions = useMemo(
    () =>
      loadTypeOptions.map((option) => ({
        ...option,
        label: optionLabel(t, option),
      })),
    [loadTypeOptions, t],
  )

  const summaryText = getOptionsSummaryText({
    stops,
    avoid: selectedAvoid,
    loadType: selectedLoadTypes,
    routingProvider,
  })

  return (
    <div style={{ opacity: isUpdating ? 0.7 : 1 }}>
      {isUpdating && <UpdateIndicator />}

      {!isMobile && (
        <div
          className={`mx-2 mb-2 flex gap-2.5 ${isUpdating ? "pointer-events-none" : "pointer-events-auto"} ${className}`}
        >
          <MultiSelect
            options={translatedAvoidOptions}
            selected={pendingAvoid}
            onChange={setPendingAvoid}
            onClose={applyAvoid}
            placeholder={t("maps.routeOptionsSelect.avoid")}
          />
          <MultiSelect
            options={translatedLoadTypeOptions}
            selected={pendingLoadTypes}
            onChange={setPendingLoadTypes}
            onClose={applyLoadTypes}
            placeholder={t("maps.routeOptionsSelect.loadType")}
          />

          <div className="flex flex-grow justify-end gap-2">
            {onTollsToggle && (
              <Button
                variant="outline"
                size="default"
                onClick={onTollsToggle}
                disabled={isUpdating}
                className="justify-between rounded-md border px-4 py-2 text-left font-medium hover:bg-blue-100 dark:hover:bg-blue-800"
              >
                {tollsToggleLabel}
              </Button>
            )}
            <ClearRouteButton />
          </div>
        </div>
      )}
      {summaryText && (
        <div className="m-1 mx-8 text-xs text-muted-foreground">
          {summaryText}
        </div>
      )}
    </div>
  )
}

export default RouteOptionsSelect
